import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { Filter, FilterBar } from '@saucelabs/ds-filters';
import {
  IconBranchOutline,
  IconProjectOutline,
  IconStatusOutline,
  IconUserOutline,
} from '@saucelabs/ds-icons';
import { Datepicker } from '@saucelabs/ds-datepicker';
import { getLocalTimeZone, today } from '@internationalized/date';
import { Item, Section } from 'react-stately';
import { debounce } from 'lodash';
import { Search } from '@saucelabs/ds-inputs';
import { useQuery } from '@apollo/client';

import { track } from '../../global/tracking';
import { actions, selectors, thunks, types, constants } from '../../ducks/filterBar';
import { useVisualDispatch, useVisualSelector } from '../../reduxHooks';
import { BuildStatus } from '../../graphql/__generated__/graphql';
import { graphql } from '../../graphql/__generated__';

const minValue = today(getLocalTimeZone()).subtract({ days: 89 });

const makeUniqueKey = (item: types.OwnerFilterItem) => `${item.type}_${item.key}`;

const ListProjects = graphql(`
  query ListProjects($filterName: String) {
    projects(filtername: $filterName)
  }
`);

const ListBranches = graphql(`
  query ListBranches($filterName: String) {
    branches(filtername: $filterName)
  }
`);

/**
 * Maps string array to filter items, removing duplicates and sorting. Uses current value to ensure
 * value is always present for Filter item processing.
 * @param values
 * @param currentValue
 */
const makeFilters = (values: (string | null)[], currentValue: string | null) => (
  (values.filter((item) => !!item) as string[])
    .concat(currentValue ? [currentValue] : [])
    // dedupe
    .filter((val, index, all) => all.indexOf(val) === index)
    .sort((item1, item2) => item1.localeCompare(item2))
    .map((value: string) => ({
      label: value,
      key: value,
    }))
);

export const BuildFilters: FC = () => {
  const dispatch = useVisualDispatch();

  const { init } = useVisualSelector((state) => state.apps.visual.filterBar);
  const searchParams = useVisualSelector(selectors.getSearchParams);

  useEffect(() => {
    if (init === 'idle') {
      void dispatch(thunks.initFilters());
    }
  }, [dispatch, init]);
  const saveFilters = useCallback(debounce(() => {
    void dispatch(thunks.saveFilters());
  }, 300, { trailing: true }), [dispatch]);
  useEffect(() => {
    if (init === 'succeeded') {
      saveFilters();
    }
  }, [saveFilters, searchParams, init]);

  // Owner
  const {
    items: ownerItems,
    loading: ownerLoading,
    value: ownerValue,
    type,
  } = useVisualSelector((state) => state.apps.visual.filterBar.owner);
  const updateOwnerSearchValue = useCallback(debounce(
    (searchValue: string) => {
      if (
        // If the state has been emptied
        searchValue.length === 0 ||
        // or they have a minimum of 3 characters for remote search
        searchValue.length >= 3
      ) {
        dispatch(actions.setOwnerSearch(searchValue));
        void dispatch(thunks.loadOwners());
      }
    },
    600,
    { trailing: true },
  ), [dispatch]);

  // Date
  const dateValue = useVisualSelector(selectors.dateFilterValue);

  // Name Search
  const nameTrackRef = useRef(false);
  const nameValue = useVisualSelector(selectors.nameValue);

  // Status
  const statusValue = useVisualSelector(selectors.statusValue);

  // Project
  const {
    value: projectValue,
    searchValue: projectSearchValue,
  } = useVisualSelector(selectors.getProjectFilter);
  const { data: projectData, loading: projectLoading } = useQuery(ListProjects, {
    variables: {
      filterName: projectSearchValue,
    },
  });
  const projectItems = useMemo(() => (
    projectData?.projects ? makeFilters(projectData.projects, projectValue) : []
  ), [projectData, projectValue]);
  const updateProjectSearchValue = useCallback(debounce(
    (searchValue: string) => {
      if (
        // If the state has been emptied
        searchValue.length === 0 ||
        // or they have a minimum of 3 characters for remote search
        searchValue.length >= 3
      ) {
        dispatch(actions.setProjectSearch(searchValue));
      }
    },
    600,
    { trailing: true },
  ), [dispatch]);

  // Branch
  const {
    value: branchValue,
    searchValue: branchSearchValue,
  } = useVisualSelector(selectors.getBranchFilter);
  const { data: branchData, loading: branchLoading } = useQuery(ListBranches, {
    variables: {
      filterName: branchSearchValue,
    },
  });
  const branchItems = useMemo(() => (
    branchData?.branches ? makeFilters(branchData.branches, branchValue) : []
  ), [branchData, branchValue]);
  const updateBranchSearchValue = useCallback(debounce(
    (searchValue: string) => {
      if (
        // If the state has been emptied
        searchValue.length === 0 ||
        // or they have a minimum of 3 characters for remote search
        searchValue.length >= 3
      ) {
        dispatch(actions.setBranchSearch(searchValue));
      }
    },
    600,
    { trailing: true },
  ), [dispatch]);

  // Create unique keys namespaced by the user type
  const keyedOwnerItems = useMemo(() => {
    const keyedItems = [...ownerItems]
      .map((item) => ({
        ...item,
        key: makeUniqueKey(item),
      }))
      .sort((itemA, itemB) => {
        if (itemA.static || itemB.static) {
          return 0;
        }

        if (itemA.label) {
          return itemA.label.localeCompare(itemB.label);
        }

        return 1;
      });

    const staticItems = keyedItems.filter((owner) => owner.static === true);

    const sectionedItems = [
      {
        key: 'static',
        label: null,
        items: staticItems,
      },
      {
        key: 'users',
        label: 'Users',
        items: keyedItems.filter((owner) => !owner.static && owner.type === 'user'),
      },
      {
        key: 'teams',
        label: 'Teams',
        items: keyedItems.filter((owner) => !owner.static && owner.type === 'team'),
      },
    ];

    return sectionedItems.filter((section) => section.items.length > 0);
  }, [ownerItems]);

  return (
    <div className="mx-2 flex flex-row gap-2 flex-wrap items-end">
      <div className="max-w-39.75 p-0.5">
        <Search
          aria-label="Search builds"
          onChange={(searchValue) => {
            // Update input value immediately
            dispatch(actions.setBuildNameValue(searchValue));
            // Async update build search value for api querying
            void dispatch(thunks.updateBuildName(searchValue));

            if (!nameTrackRef.current) {
              track.filterClicked({
                filter_type: 'search',
              });
              nameTrackRef.current = true;
            }
          }}
          value={nameValue}
        />
      </div>
      <FilterBar
        onReset={() => dispatch(actions.resetFilters())}
        showResetButton
      >
        <>
          <Datepicker
            minValue={minValue}
            onChange={(value) => {
              const { start, end } = value;
              dispatch(actions.setDate({
                start: start.toString(),
                end: end.toString(),
              }));
              track.filterClicked({
                filter_type: 'date',
                filter_value: [start.toString(), end.toString()],
              });
            }}
            value={dateValue}
          />
        </>
        <>
          <Filter
            data-qa="owner-filter"
            disallowEmptySelection
            hasSearch
            isLoading={ownerLoading}
            items={keyedOwnerItems}
            label="Owner"
            name="owner"
            onSearchChange={updateOwnerSearchValue}
            onSelectionChange={(keys) => {
              const first = [...keys].find((i) => i);
              const owner = ownerItems.find((i) => (
                makeUniqueKey(i) === first
              ));
              dispatch(actions.setOwner(owner || null));
              track.filterClicked({
                filter_type: 'owner',
                filter_value: [...keys],
              });
            }}
            prefixIcon={IconUserOutline}
            selectedKeys={ownerValue && type ? [`${type}_${ownerValue}`] : []}
            selectionBehavior="replace"
            selectionMode="single"
          >
            {(section) => (
              <Section
                key={section.key}
                items={section.items}
                title={section.label}
              >
                {(item) => (
                  <Item key={item.key}>
                    {item.label}
                  </Item>
                )}
              </Section>
            )}
          </Filter>
        </>
        <>
          <Filter
            data-qa="status-filter"
            items={constants.STATUSES}
            label="Status"
            name="status"
            onSelectionChange={(keys) => {
              const value = keys === 'all' ? constants.STATUSES.map((val) => (
                val.key
              )) : [...keys] as BuildStatus[];
              dispatch(actions.setStatus(value));
              track.filterClicked({
                filter_type: 'status',
                filter_value: value,
              });
            }}
            prefixIcon={IconStatusOutline}
            selectedKeys={statusValue}
            selectionMode="multiple"
          >
            {(item) => (
              <Item
                key={item.key}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                prefixIcon={item.prefixIcon}
              >
                {item.label}
              </Item>
            )}
          </Filter>
        </>
        <>
          <Filter
            data-qa="project-filter"
            hasSearch
            isLoading={projectLoading}
            items={projectItems}
            label="Project"
            name="project"
            onSearchChange={updateProjectSearchValue}
            onSelectionChange={(keys) => {
              const first = [...keys].find((i) => i);
              dispatch(actions.setProject(first ? String(first) : null));
              track.filterClicked({
                filter_type: 'project',
                filter_value: [...keys],
              });
            }}
            prefixIcon={IconProjectOutline}
            selectedKeys={projectValue ? [projectValue] : []}
            selectionBehavior="replace"
            selectionMode="single"
          >
            {(item) => (
              <Item key={item.key}>
                {item.label}
              </Item>
            )}
          </Filter>
        </>
        <>
          <Filter
            data-qa="branch-filter"
            hasSearch
            isLoading={branchLoading}
            items={branchItems}
            label="Branch"
            name="branch"
            onSearchChange={updateBranchSearchValue}
            onSelectionChange={(keys) => {
              const first = [...keys].find((i) => i);
              dispatch(actions.setBranch(first ? String(first) : null));
              track.filterClicked({
                filter_type: 'branch',
                filter_value: [...keys],
              });
            }}
            prefixIcon={IconBranchOutline}
            selectedKeys={branchValue ? [branchValue] : []}
            selectionBehavior="replace"
            selectionMode="single"
          >
            {(item) => (
              <Item key={item.key}>
                {item.label}
              </Item>
            )}
          </Filter>
        </>
      </FilterBar>
    </div>
  );
};
